<template>
  <div>
    <TheHubSkeleton v-if="loading" :cards="Math.min(articles.length || 6, 6)" />
    <TheHubErrorMessage v-else-if="error" title="Sorry, we couldn’t load the articles" :show-back-link="$route.path !== '/the-hub'" @retry="$emit('retry')" />
    <p v-else-if="!articles.length" class="the-hub__empty">No articles found.</p>

    <ul v-if="!loading" class="row post-list">
      <li v-for="article in articles" :key="article.post_id" class="col-12 col-md-6 col-xl-4 item">
        <div class="post-box">
          <div class="item">
            <h2>
              <nuxt-link :to="articleLink(article)">{{ article.post_title }}</nuxt-link>
            </h2>
            <div class="post-meta post-meta--date">{{ formatDate(article.post_date) }}</div>
            <div class="post-meta">
              <span><font-awesome-icon :icon="['fas', 'user']" /> magazine.co.uk</span>
              <span v-if="(article.categories || []).length">
                <font-awesome-icon :icon="['fas', 'folder']" />
                <span v-for="(category, index) in article.categories" :key="category.category_id" class="post-meta__category"
                  ><template v-if="index > 0">, </template><a :href="categoryUrl(category.slug)">{{ categoryName(category) }}</a></span
                >
              </span>
            </div>
            <div class="post-entry entry">
              <!-- Sanitized teaser HTML (image, heading and intro up to the WordPress more marker) -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-if="article.teaser" class="article-teaser" v-html="article.teaser"></div>
              <nuxt-link :to="articleLink(article)" class="read-more">Continue reading <span class="meta-nav">&rarr;</span></nuxt-link>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <div v-if="showPagination" class="pagination-container" aria-label="Page navigation">
      <client-only>
        <paginate
          v-model="currentPage"
          :page-count="totalPages"
          :page-range="3"
          :margin-pages="0"
          :hide-prev-next="true"
          :prev-text="'Prev'"
          :next-text="'Next'"
          :container-class="'pagination justify-content-center py-4'"
          :page-class="'page-item'"
          :page-link-class="'page-link'"
          :prev-class="'page-item'"
          :prev-link-class="'page-link'"
          :next-class="'page-item'"
          :next-link-class="'page-link'"
          :click-handler="onPageChange"
        >
        </paginate>
      </client-only>
    </div>
  </div>
</template>

<script>
import { formatArticleDate, articlePath } from '~/lib/util/articles'
import { getCategoryBySlug, categoryPath } from '~/lib/data/the-hub-categories'
import TheHubSkeleton from '~/components/TheHub/Skeleton.vue'
import TheHubErrorMessage from '~/components/TheHub/ErrorMessage.vue'

export default {
  name: 'TheHubArticleList',
  components: {
    TheHubSkeleton,
    TheHubErrorMessage
  },
  props: {
    articles: { type: Array, required: true },
    loading: { type: Boolean, required: false, default: false },
    error: { type: Boolean, required: false, default: false },
    totalPages: { type: Number, required: true },
    // Category being browsed: the article page uses it for its previous/next links.
    categorySlug: { type: String, required: false, default: '' },
    page: { type: Number, required: true }
  },
  data() {
    return { currentPage: this.page }
  },
  computed: {
    // Only when there really is another page to go to (not for errors, empty or out-of-range pages).
    showPagination() {
      return this.totalPages > 1 && !this.error && (this.loading || this.articles.length > 0)
    }
  },
  watch: {
    page(value) {
      this.currentPage = value
    }
  },
  methods: {
    articleLink(article) {
      return { path: articlePath(article), query: this.categorySlug ? { category: this.categorySlug } : {} }
    },
    formatDate: formatArticleDate,
    categoryUrl(slug) {
      return `${this.$config.baseURL}${categoryPath({ slug })}`
    },
    // The API returns names with HTML entities (e.g. "Food &amp; Drink"); prefer the static catalog name.
    categoryName(category) {
      return getCategoryBySlug(category.slug)?.name || String(category.name).replace(/&amp;/g, '&')
    },
    onPageChange(pageNum) {
      this.$emit('change-page', pageNum)
    }
  }
}
</script>

<style lang="scss" scoped>
.the-hub__empty {
  margin-top: 20px;
}
.pagination-container {
  display: flex;
  justify-content: center;
}
.post-list {
  list-style: none;
  padding: 0;
  margin: 0;
  // Cards keep their natural height (no equal-height stretching).
  align-items: flex-start;
}
.post-box {
  display: block;
  margin-top: 30px;
  > .item {
    position: relative;
    padding: 20px 18px 30px;
    cursor: pointer;
    border: 1px solid #e9e9e9;
    border-radius: 4px;
    transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
    &:hover,
    &:focus-within {
      border-color: #b5b5b5;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
      transform: translateY(-2px);
    }
  }
  h2 {
    font-size: 22px;
    font-weight: 400;
    line-height: 1.25;
    margin: 0 0 15px;
    // Keep the title colour on hover (the-hub-style.css greys `.post-list h2 a:hover`).
    a,
    a:hover,
    a:focus {
      color: #363636;
      text-decoration: none;
    }
    // Stretched link: the title link covers the whole card, so a click anywhere opens the article.
    a::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
    }
  }
  .post-meta {
    font-style: normal;
    font-size: 14px;
    line-height: 21px;
    &--date {
      padding-left: 9px;
      margin-bottom: 2px;
    }
    span {
      display: inline-block;
      margin-right: 26px;
    }
    .post-meta__category {
      display: inline;
      margin-right: 0;
    }
    a {
      color: #363636;
    }
    // Category links stay clickable above the card-wide link.
    .post-meta__category a {
      position: relative;
      z-index: 2;
    }
  }
  .post-entry {
    max-width: 100%;
    margin: 15px 0 0;
    height: auto;
    overflow: visible;
  }
  .read-more {
    display: table;
    margin: 5px auto 0;
    padding: 12px 20px !important;
    font-size: 15px;
    line-height: 1;
    text-transform: uppercase;
    background-color: #5cb85c;
    border-bottom-color: #4cae4c;
  }
}

// Teaser HTML injected through v-html, always: cover (fixed box), title, one intro paragraph.
.article-teaser {
  ::v-deep {
    .the-hub-card__cover {
      width: 196px;
      max-width: 100%;
      margin: 0 auto 25px;
      padding: 0;
      a {
        display: block;
      }
      // Height follows the img width/height attributes (the image's own ratio at 196px wide).
      img {
        display: block;
        width: 100%;
        height: auto;
        object-fit: contain;
        margin: 0;
        padding: 0;
        border: 0;
      }
    }
    .the-hub-card__title {
      color: #363636;
      font-size: 26px;
      font-weight: 700;
      line-height: 1.15;
      text-align: center;
      margin: 0 0 15px;
    }
    .the-hub-card__intro {
      font-size: 15px;
      line-height: 22px;
      margin: 0 0 15px;
      padding: 0;
    }
  }
}
</style>
